'use client';

import React, { useState } from 'react';

import { PlayCircle } from 'lucide-react';

import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

import VideoEmbed from '../Video/VideoPlayer';

export type VideoItem = {
    title: string;
    sourceType: 'external' | 'upload';
    url?: string;
    videoFile?: { url: string };
    thumbnail?: { url: string; alt?: string };
    duration?: string;
};

export type VideoListBlockProps = {
    title?: string;
    items: VideoItem[];
    className?: string;
};

export const VideoListBlock: React.FC<VideoListBlockProps> = ({ title, items, className }) => {
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

    if (!items || items.length === 0) return null;

    const currentVideo = items[currentVideoIndex];
    const videoUrl = currentVideo.sourceType === 'external' ? currentVideo.url : currentVideo.videoFile?.url;

    return (
        <div className={cn('w-full my-8 grid grid-cols-1 lg:grid-cols-3 gap-6', className)}>
            {/* Main Player (Left/Top) */}
            <div className="lg:col-span-2 flex flex-col gap-4">
                <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-lg">
                    {videoUrl ? (
                        <VideoEmbed
                            sourceType={currentVideo.sourceType}
                            url={currentVideo.url}
                            media={currentVideo.videoFile}
                            caption={currentVideo.thumbnail?.alt}
                            blockType="video"
                            align="center"
                            className="absolute top-0 left-0 right-0 bottom-0 max-w-none! m-0!"
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center text-white/50">Видео недоступно</div>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <h3 className="text-xl font-bold leading-tight">{currentVideo.title}</h3>
                </div>
            </div>

            {/* Video List (Right/Bottom) */}
            <div className="lg:col-span-1 flex flex-col  max-h-[366px]  xl:max-h-[462px] 2xl:max-h-[558px] rounded-xl border bg-card text-card-foreground shadow-sm overflow-hidden">
                <div className="px-3 py-2! border-b bg-muted/30">
                    <h4 className="font-semibold text-lg text-primary m-0! p-0!">{title || 'Все видео'}</h4>
                </div>
                <ScrollArea className="flex-1 max-h-[366px]  xl:max-h-[462px] 2xl:max-h-[558px]">
                    {items.map((item, index) => {
                        const isActive = index === currentVideoIndex;
                        return (
                            <button
                                key={index}
                                onClick={() => setCurrentVideoIndex(index)}
                                className={cn(
                                    'flex gap-3 items-start p-2 rounded-lg text-left transition-all w-full duration-200 group',
                                    isActive ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50'
                                )}
                            >
                                <div className="relative w-24 aspect-video shrink-0 rounded-md overflow-hidden bg-muted">
                                    {item.thumbnail?.url ? (
                                        <img
                                            src={item.thumbnail.url}
                                            alt={item.thumbnail.alt || item.title}
                                            className="w-full h-full object-fill m-0! p-0!"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-secondary">
                                            <PlayCircle className="w-6 h-6 text-muted-foreground" />
                                        </div>
                                    )}
                                    {isActive && (
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1 min-w-0">
                                    <span
                                        className={cn(
                                            'text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors',
                                            isActive && 'text-primary'
                                        )}
                                    >
                                        {item.title}
                                    </span>
                                    {item.duration && (
                                        <span className="text-xs text-muted-foreground">{item.duration}</span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </ScrollArea>
            </div>
        </div>
    );
};
